import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { NAV, COMPANY } from "@/lib/content";
import { scrollToId } from "@/lib/nav";

const LOGO = `${process.env.PUBLIC_URL}/amanah-main-logo.png`;

export default function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (section) => {
    if (section === "top") {
      if (pathname !== "/") navigate("/");
      else scrollToId("top");
      return;
    }
    if (pathname !== "/") navigate("/", { state: { scrollTo: section } });
    else scrollToId(section);
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-[box-shadow,border-color] duration-300 ${
        scrolled ? "border-b border-line shadow-[0_1px_20px_rgba(11,25,44,0.06)]" : "border-b border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 sm:px-10 lg:px-16">
        <Link
          to="/"
          onClick={() => pathname === "/" && scrollToId("top")}
          className="flex items-center py-1"
          data-testid="nav-logo-link"
          aria-label={`${COMPANY.shortName} home`}
        >
          <img
            src={LOGO}
            alt={`${COMPANY.name} — ${COMPANY.strapline}`}
            className="h-12 w-auto sm:h-14"
            width="180"
            height="128"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) =>
            item.section ? (
              <button
                key={item.label}
                onClick={() => goSection(item.section)}
                className="group relative text-sm font-medium text-navy/80 transition-colors hover:text-navy"
                data-testid={`nav-${item.section}`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-green transition-all duration-300 group-hover:w-full" />
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className="group relative text-sm font-medium text-navy/80 transition-colors hover:text-navy"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-green transition-all duration-300 group-hover:w-full" />
              </Link>
            )
          )}
          <Link
            to="/book-a-consultation"
            className="inline-flex items-center gap-2 bg-brand-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-greenDeep"
            data-testid="nav-book-consultation"
          >
            Book a Consultation
            <ArrowRight className="h-4 w-4" />
          </Link>
        </nav>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-11 w-11 items-center justify-center text-navy"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm border-l border-line bg-white p-0">
              <div className="flex items-center justify-between border-b border-line px-6 py-4">
                <img src={LOGO} alt={COMPANY.name} className="h-11 w-auto" />
                <SheetClose asChild>
                  <button className="inline-flex h-10 w-10 items-center justify-center text-navy" aria-label="Close menu">
                    <X className="h-6 w-6" />
                  </button>
                </SheetClose>
              </div>
              <nav className="flex flex-col px-6 py-4" aria-label="Mobile">
                {NAV.map((item) => (
                  <SheetClose asChild key={item.label}>
                    {item.section ? (
                      <button
                        onClick={() => goSection(item.section)}
                        className="border-b border-line py-4 text-left text-lg font-medium text-navy"
                        data-testid={`mobile-nav-${item.section}`}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        to={item.to}
                        className="border-b border-line py-4 text-left text-lg font-medium text-navy"
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    to="/book-a-consultation"
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-green px-5 py-3.5 text-base font-semibold text-white"
                    data-testid="mobile-book-consultation"
                  >
                    Book a Consultation
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/join-our-team"
                    className="mt-3 inline-flex items-center justify-center gap-2 border border-navy px-5 py-3.5 text-base font-semibold text-navy"
                    data-testid="mobile-join-team"
                  >
                    Join Our Fundraising Team
                  </Link>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
